import type { Language } from '@seek/forms-ui/lib/types';
import { useTranslations } from '@vocab/react';
import { Alert, Text, TextLink } from 'braid-design-system';
import React, { useEffect } from 'react';

import { employerSignInHelpLink } from '../../../../../constants';
import { useAnalyticsContext } from '../../../../../modules/analytics/AnalyticsContext';
import type { ContactUs } from '../../../../../modules/customisation/types';
import arrayIncludes from '../../../../../utils/arrayIncludes';
import getDisplayBrand from '../../../../../utils/getDisplayBrand';
import getHashRoute from '../../../../../utils/getHashRoute';
import { getLanguageTwoCharsOnly } from '../../../../../utils/getLanguageSuffix';
import { getErrorCode } from '../../../../../utils/validateLoginMessage';
import { useAppContext } from '../../../../AppContext';
import { FORGOT_PASSWORD } from '../../hashRoutes';

import translations from './.vocab';
import { criticalMessages, type Message, errorMessages } from './types';

type Tones = 'critical' | 'caution' | 'positive' | null;

const getMessageTone = (message: Message): Tones => {
  if (arrayIncludes(message, criticalMessages)) {
    return 'critical';
  }
  if (arrayIncludes(message, errorMessages)) {
    return 'caution';
  }
  return 'positive';
};

interface Props {
  message: Message | null;
}

const LoginMessage = React.memo(({ message }: Props) => {
  const { t } = useTranslations(translations);
  const {
    analytics: { trackLink },
  } = useAnalyticsContext();

  const tone: Tones = message ? getMessageTone(message) : null;

  const { localeConfig, experience, language, locale, brand } = useAppContext();
  const displayBrand = getDisplayBrand(brand);

  const onlyLanguage: Language = getLanguageTwoCharsOnly(language);

  const contactUsLink =
    localeConfig?.[locale]?.[experience]?.contactUsLinks?.[
      onlyLanguage as keyof ContactUs
    ] ?? '';

  const { errorCode, errorSubCode } = getErrorCode(message!);

  const translatedMessage = errorCode
    ? (t(errorCode as any, {
        brand: displayBrand,
        Link: (children: any) => (
          <TextLink href={contactUsLink} target="_blank" key="contactUsLink">
            {children}
          </TextLink>
        ),
        errorSubCode,
      }) as string)
    : null;

  useEffect(() => {
    if (!translatedMessage || !tone) {
      return;
    }

    trackLink({
      eventName: 'alert_viewed',
      alertShownType: 'alert',
      alertShownTone: tone,
      alertShownMessage: translatedMessage,
      alertShownPosition: 'content',
      alertShownTitle: 'Error with login',
    });
  }, [trackLink, translatedMessage, tone]);

  if (!translatedMessage || !tone) {
    return null;
  }

  if (
    arrayIncludes(message, [
      'login_msg.reset.failure.reused_link',
      'login_msg.reset.failure.access_expired',
    ] as Message[])
  ) {
    return (
      <Alert tone={tone}>
        <Text>
          {translatedMessage}&nbsp;
          <TextLink key="link" href={getHashRoute(FORGOT_PASSWORD)}>
            {t('Request again')}
          </TextLink>
        </Text>
      </Alert>
    );
  } else if (message === 'error.email_verification_required') {
    return (
      <Alert tone={tone}>
        <Text>
          {translatedMessage}
          <br />
          <TextLink
            key="employerSignInHelpLink"
            target="_blank"
            href={employerSignInHelpLink}
          >
            {t('Didnt receive the email')}
          </TextLink>
        </Text>
      </Alert>
    );
  }

  return (
    <Alert tone={tone}>
      <Text>{translatedMessage}</Text>
    </Alert>
  );
});

export default LoginMessage;
